import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import type { IField } from '../../services/metadataService';
import {
  NEW_VISIBILITY_ROW,
  isBooleanController,
  type VisibilityDraft,
  type VisibilityMode,
  type VisibilityOperator,
  type VisibilityRowDraft,
} from './orderComponentVisibilityDraft';

interface VisibilityRulesEditorProps {
  draft: VisibilityDraft;
  candidates: IField[];
  allowsAllowlist: boolean;
  maxAllowlistSize: number;
  onChange: (draft: VisibilityDraft) => void;
}

const MODES: { key: VisibilityMode; label: string }[] = [
  { key: 'always', label: 'Always visible' },
  { key: 'Show', label: 'Show only when' },
  { key: 'Hide', label: 'Hide when' },
];

const OPERATORS: { key: VisibilityOperator; label: string }[] = [
  { key: 'equals', label: 'Equals' },
  { key: 'in', label: 'Is one of' },
];

const BOOLEAN_VALUES = [
  { key: 'true', label: 'Yes' },
  { key: 'false', label: 'No' },
];

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  condition: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  conditionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  conditionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  fullRow: {
    gridColumn: '1 / -1',
  },
  addRow: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
});

const VisibilityRulesEditor = ({
  draft,
  candidates,
  allowsAllowlist,
  maxAllowlistSize,
  onChange,
}: VisibilityRulesEditorProps) => {
  const styles = useStyles();
  const operators = allowsAllowlist ? OPERATORS : OPERATORS.slice(0, 1);

  const updateRow = (index: number, patch: Partial<VisibilityRowDraft>) =>
    onChange({
      ...draft,
      rows: draft.rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      ),
    });

  const changeMode = (mode: VisibilityMode) =>
    onChange({
      ...draft,
      mode,
      rows:
        mode !== 'always' && draft.rows.length === 0
          ? [NEW_VISIBILITY_ROW]
          : draft.rows,
    });

  const modeLabel = MODES.find((mode) => mode.key === draft.mode)?.label ?? '';

  if (!draft.editable) {
    return (
      <div className={styles.section}>
        <Text className={styles.title}>Visibility</Text>
        <Text role="status">
          This component has an existing visibility rule that cannot be edited
          here. It is kept unchanged when you save.
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <Text className={styles.title}>Visibility</Text>
      <Field
        hint={
          draft.mode !== 'always' && draft.rows.length > 1
            ? 'All conditions must match.'
            : undefined
        }
      >
        <Dropdown
          aria-label="Visibility"
          value={modeLabel}
          selectedOptions={[draft.mode]}
          onOptionSelect={(_event, data) =>
            changeMode(data.optionValue as VisibilityMode)
          }
        >
          {MODES.map((mode) => (
            <Option key={mode.key} value={mode.key}>
              {mode.label}
            </Option>
          ))}
        </Dropdown>
      </Field>
      {draft.mode !== 'always' &&
        draft.rows.map((row, index) => {
          const controller = candidates.find(
            (candidate) =>
              candidate.name.toLowerCase() === row.controller.toLowerCase()
          );
          const booleanValue = isBooleanController(controller);
          const hasOperatorChoice = operators.length > 1;
          const valueField =
            row.operator === 'in' ? (
              <Field
                label="Values"
                hint={`One value per line, up to ${maxAllowlistSize}.`}
                className={styles.fullRow}
              >
                <Textarea
                  aria-label={`Condition ${index + 1} values`}
                  value={row.text}
                  rows={3}
                  onChange={(_event, data) =>
                    updateRow(index, { text: data.value })
                  }
                />
              </Field>
            ) : booleanValue ? (
              <Field
                label="Value"
                className={hasOperatorChoice ? styles.fullRow : undefined}
              >
                <Dropdown
                  aria-label={`Condition ${index + 1} value`}
                  placeholder="Select a value"
                  value={
                    BOOLEAN_VALUES.find((value) => value.key === row.text)
                      ?.label ?? ''
                  }
                  selectedOptions={row.text ? [row.text] : []}
                  onOptionSelect={(_event, data) =>
                    updateRow(index, { text: data.optionValue ?? '' })
                  }
                >
                  {BOOLEAN_VALUES.map((value) => (
                    <Option key={value.key} value={value.key}>
                      {value.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            ) : (
              <Field
                label="Value"
                className={hasOperatorChoice ? styles.fullRow : undefined}
              >
                <Input
                  aria-label={`Condition ${index + 1} value`}
                  value={row.text}
                  onChange={(_event, data) =>
                    updateRow(index, { text: data.value })
                  }
                />
              </Field>
            );
          return (
            <div key={index} className={styles.condition}>
              <div className={styles.conditionHeader}>
                <Text className={styles.conditionTitle}>
                  Condition {index + 1}
                </Text>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DeleteRegular />}
                  aria-label={`Remove condition ${index + 1}`}
                  onClick={() =>
                    onChange({
                      ...draft,
                      rows: draft.rows.filter(
                        (_row, rowIndex) => rowIndex !== index
                      ),
                    })
                  }
                >
                  Remove
                </Button>
              </div>
              <div className={styles.grid}>
                <Field label="Field">
                  <Dropdown
                    aria-label={`Condition ${index + 1} field`}
                    placeholder="Select a field"
                    value={controller?.label || controller?.name || ''}
                    selectedOptions={controller ? [controller.name] : []}
                    onOptionSelect={(_event, data) =>
                      updateRow(index, {
                        controller: data.optionValue ?? '',
                        text: '',
                      })
                    }
                  >
                    {candidates.map((candidate) => (
                      <Option
                        key={candidate.name}
                        value={candidate.name}
                        text={candidate.label || candidate.name}
                      >
                        {candidate.label || candidate.name}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
                {hasOperatorChoice ? (
                  <Field label="Operator">
                    <Dropdown
                      aria-label={`Condition ${index + 1} operator`}
                      value={
                        operators.find(
                          (operator) => operator.key === row.operator
                        )?.label ?? ''
                      }
                      selectedOptions={[row.operator]}
                      onOptionSelect={(_event, data) =>
                        updateRow(index, {
                          operator: data.optionValue as VisibilityOperator,
                          text: '',
                        })
                      }
                    >
                      {operators.map((operator) => (
                        <Option key={operator.key} value={operator.key}>
                          {operator.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                ) : null}
                {valueField}
              </div>
            </div>
          );
        })}
      {draft.mode !== 'always' && (
        <div className={styles.addRow}>
          <Button
            appearance="subtle"
            icon={<AddRegular />}
            onClick={() =>
              onChange({ ...draft, rows: [...draft.rows, NEW_VISIBILITY_ROW] })
            }
          >
            Add condition
          </Button>
        </div>
      )}
    </div>
  );
};

export default VisibilityRulesEditor;
